import { Box, Grid, Paper, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../context";
import { useProfile } from "../../../context/ProfileContext";
import { ResponsibleModal } from "../../../shared/components";
import { ToolbarSinister } from "../../../shared/components/toolbarSinister/toolbarSinister";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { ProvisionService } from "../../../shared/services/api/provisoes/ProvisionService";
import { SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { userService } from "../../../shared/services/api/user/userService";
import { getFollowUpServiceByContext } from "../../client/components/ModalFollowUpGeneric/FollowUpService";
import { GenericFollowUpModalContent } from "../../client/components/ModalFollowUpGeneric/GenericFollowUpModalContent";
import { ModalAddValues } from "./Modals/ModalAddValues";
import { ModalAllDocumentsValues } from "./Modals/ModalAllDocumentsValues";
import { ModalDocumentsSinister } from "./Modals/ModalDocumentsSinister";
import { ModalGenerateBordero } from "./Modals/ModalGenerateBordero";
import { SinisterBasicCardData } from "./SinisterBasicCardData/SinisterBasicCardData";
import { TableBordero } from "./TableBordero";
import { TableSinisterValues } from "./TableSinisterValues";
import { ToolbarSinisterValues } from "./ToolbarSinisterValues";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

export const SinisterValuesPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const toastHandler = useToast();
	const { loggedUser } = useAuthContext();
	const { isSuperAdmin, permissionLevel } = useProfile();
	const followUpServices = getFollowUpServiceByContext("sinister");
	const [statusSinister, setStatusSinister] = useState("");
	const [busyProperty, setBusyProperty] = useState("");
	const [rows, setRows] = useState<any>(null);
	const [sumLmi, setSumLmi] = useState<any>(null);
	const [totalBordero, setTotalBordero] = useState(0);
	const [responsibleName, setResponsibleName] = useState("Nenhum responsável designado");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [refreshTable, setRefreshTable] = useState(false);
	const [value, setValue] = useState(0);
	const [showButtonOpenSinister, setShowButtonOpenSinister] = useState(false);
	const [sinisterValuesCount, setSinisterValuesCount] = useState(0);
	const [currentValueRows, setCurrentValueRows] = useState<any[]>([]);

	const canViewBorderoTab = checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.ViewBordero);

	const [modals, setModals] = useState({
		value: false,
		responsible: false,
		followup: false,
		checklist: false,
		bordero: false,
		documentsValues: false,
	});

	const canShowAttachmentButton = () => {
		const statusPermitidos = [
			"open",
			"in_progress",
			"in_analysis",
			"debts_confession",
			"monthly_compensation",
			"pending",
			"sketch",
			"according",
		];
		return statusPermitidos.includes(statusSinister);
	};

	const handleToggleModal = (modal: keyof typeof modals, isOpen: boolean) => {
		setModals((prev) => ({ ...prev, [modal]: isOpen }));
	};

	const handleRefreshTable = () => setRefreshTable((prev) => !prev);

	const handleFetchResponsible = async (responsibleId: string) => {
		try {
			const resultUser = await userService.getByID(responsibleId);
			if (!(resultUser instanceof Error)) {
				setResponsibleName(resultUser.name);
			}
		} catch (error) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro ao buscar responsável",
				position: "top-center",
			});
		}
	};

	const handleFetchTotalBordero = async (sinisterId: string) => {
		try {
			const result = await ProvisionService.countBordero(sinisterId);
			if (!(result instanceof Error)) setTotalBordero(result);
			else setTotalBordero(0);
		} catch (error) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro ao buscar total de borderôs",
				position: "top-center",
			});
		}
	};

	const handleGetSinister = async () => {
		try {
			const result = await SinisterService.getById(`${id}`);

			if (!(result instanceof Error)) {
				setRows(result);
				setStatusSinister(result.status ?? "");
				setBusyProperty(result?.busyProperty ?? "");

				if (result?.responsibleId) handleFetchResponsible(result.responsibleId);
				if (result?.id) handleFetchTotalBordero(result.id);
			} else {
				console.error("Erro ao buscar sinistro:", result.message);
				toastHandler.present({
					type: "error",
					messageOrContent: "Erro ao buscar dados do sinistro.",
					position: "top-center",
				});
			}

			const lmi = await SinisterService.totalLmi(`${id}`);
			if (!(lmi instanceof Error)) {
				setSumLmi(lmi.data);
			} else {
				console.error("Erro ao calcular LMI:", lmi.message);
				toastHandler.present({
					type: "error",
					messageOrContent: "Erro ao calcular LMI.",
					position: "top-center",
				});
			}
		} catch (error: any) {
			console.error("Erro inesperado:", error);
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro inesperado ao carregar informações do sinistro.",
				position: "top-center",
			});
		}
	};

	const handleOpenSinister = (id: string) => {
		SinisterService.confirmOpenSinister(id).then((result) => {
			toastHandler.present({
				type: "success",
				messageOrContent: "Registro gravado com sucesso",
				position: "top-right",
			});

			setStatusSinister(result.status);
			// Recarrega dados completos do sinistro após confirmar abertura
			handleGetSinister();
		});
	};

	useEffect(() => {
		if (id !== "novo") handleGetSinister();
	}, [id]);

	useEffect(() => {
		if (statusSinister === "sketch" && sinisterValuesCount > 0) {
			setShowButtonOpenSinister(true);
		} else {
			setShowButtonOpenSinister(false);
		}
	}, [statusSinister, sinisterValuesCount]);

	useEffect(() => {
		if (!canViewBorderoTab && value === 1) {
			setValue(0);
		}
	}, [canViewBorderoTab, value]);

	const renderModals = () => (
		<>
			{modals.value && (
				<ModalAddValues onClose={() => handleToggleModal("value", false)} open={modals.value} sinisterId={id} onSuccess={handleRefreshTable} />
			)}

			{modals.responsible && (
				<ResponsibleModal
					isOpenModal={modals.responsible}
					isCloseModal={() => handleToggleModal("responsible", false)}
					onclickConfirm={() => handleToggleModal("responsible", false)}
					idSinister={id}
				/>
			)}

			{modals.checklist && (
				<ModalDocumentsSinister
					isOpen={modals.checklist}
					onClose={() => handleToggleModal("checklist", false)}
					id={id}
					busyProperty={busyProperty}
					showButtonAttachment={canShowAttachmentButton()}
				/>
			)}

			{modals.bordero && (
				<ModalGenerateBordero
					open={modals.bordero}
					onClose={() => handleToggleModal("bordero", false)}
					onSuccess={handleRefreshTable}
					rows={selectedRows}
					clientId={rows?.clientId ?? ""}
					sinisterId={id}
				/>
			)}

			{modals.documentsValues && (
				<ModalAllDocumentsValues
					open={modals.documentsValues}
					onClose={() => handleToggleModal("documentsValues", false)}
					sinisterId={id}
					valueRows={currentValueRows}
				/>
			)}

			{modals.followup && (
				<GenericFollowUpModalContent
					open={modals.followup}
					onClose={() => handleToggleModal("followup", false)}
					clientId={id}
					loggedUser={loggedUser!}
					services={followUpServices}
					permissionSend={true}
					permissionAddContact={true}
				/>
			)}
		</>
	);

	return (
		<LayoutBaseDePagina
			titulo="Sinistros"
			subtitulo={`Detalhes do Sinistro [ ${rows?.proposal?.name || id} ]`}
			barraDeFerramentas={
				<ToolbarSinister
					showButtonCancel={checkByProfileGuard(PermissionRolesEnum.SinisterOptions, PermissionActionsEnum.Cancel)}
					showButtonValues={true}
					showSelectedValue="contained"
					showButtonValuesLoading={false}
					showButtonDocument={true}
					clickButtonBack={() => navigate(`/sinistros/detalhe/${id}`)}
					showButtonValid={false}
					textButtonConfirm="Confirmar Abertura"
					showButtonConfirm={showButtonOpenSinister ? true : false}
					clickButtonConfirm={() => {
						handleOpenSinister(id);
					}}
					clickButtonResponsible={() => handleToggleModal("responsible", true)}
					clickButtonDocument={() => handleToggleModal("checklist", true)}
					showButtonFinish={false}
					showButtonResponsible={!!(isSuperAdmin || (permissionLevel != null && permissionLevel < 5))}
					clickButtonFollowUp={() => handleToggleModal("followup", true)}
				/>
			}
		>
			{renderModals()}

			<Grid container spacing={2}>
				{rows && (
					<Grid item xs={12}>
						<SinisterBasicCardData row={rows} responsibleName={responsibleName} totalBordero={totalBordero} sumLmi={sumLmi} />
					</Grid>
				)}

				<Grid item xs={12}>
					<Box component={Paper} sx={{ p: 2, mx: 1, borderRadius: 4, overflowX: "hidden" }}>
						<Box display="flex">
						<Box sx={{ borderBottom: 1, borderColor: "divider", width: "40%" }}>
							<Tabs value={value} onChange={(_, newValue) => setValue(newValue)}>
								<Tab label="Valores" {...a11yProps(0)} />
								{canViewBorderoTab && <Tab label="Borderôs" {...a11yProps(1)} />}
							</Tabs>
						</Box>
						<Box sx={{ borderBottom: 1, borderColor: "divider", width: "60%" }} display="flex" justifyContent="flex-end">
							<ToolbarSinisterValues
								onClickAddNew={() => handleToggleModal("value", true)}
								onClickGenerateBordero={() => handleToggleModal("bordero", true)}
								onClickAttachments={() => handleToggleModal("documentsValues", true)}
								selectedIds={new Set(selectedRows.map((r) => r.id!))}
								showButtonNew={canShowAttachmentButton()}
								showButtonAttachments={canShowAttachmentButton()}
								attachmentsDisabled={currentValueRows.length === 0}
							/>
						</Box>
					</Box>

					<TabPanel value={value} index={0}>
						<TableSinisterValues
							SinisterId={id}
							refreshTrigger={refreshTable}
							onSelectionChange={setSelectedRows}
							onRowCountChange={(count: number) => setSinisterValuesCount(count)}
							onRowsLoaded={setCurrentValueRows}
						/>
					</TabPanel>

					{canViewBorderoTab && (
						<TabPanel value={value} index={1}>
							<TableBordero SinisterId={id} />
						</TabPanel>
					)}
					</Box>
				</Grid>
			</Grid>
		</LayoutBaseDePagina>
	);
};
